import moment from 'moment';
import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { compose } from 'recompose';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { DeleteConfirmModal } from 'components/FormModal';
import PageHeaderIcon from 'components/PageHeaderIcon';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import ThemedSelect from 'components/ThemedSelect';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { useGetAllAccounts } from 'pages/app/Credits/gql/queries/useGetAllAccounts';
import DeleteAnnualTicket from '../AddAnnualTickets/gql/DeleteAnnualTicket';
import { useDuplicateAnnual } from '../AddAnnualTickets/gql/DuplicateAnnualTicket';
import { useGetAllAnnualTickets } from '../gql/UseGetAllAnnualTickets';
import {
  PageWrapper,
  Container,
  DashboardLayout,
  MainContent,
  Sidebar,
  HeaderContainer,
  HeaderTitle,
  ContentWrapper,
  PageHeader,
  FilterRow,
  FilterContainer,
  StatusBadge,
  TicketName,
  BackButton,
} from './StyledComponents';

// Back arrow icon
const BackArrowIcon = () => (
  <svg
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

// Plus icon for create button
const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 4v16m8-8H4"
    />
  </svg>
);

// Duplicate icon for action
const DuplicateIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeWidth="2" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
    />
  </svg>
);

const AnnualTicketsPage = (props) => {
  const { deleteAnnualTicket } = props;
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Get current year
  const currentYear = Number(moment().format('YYYY'));

  // Filter state - local instead of context for simpler management
  const [accountFilter, setAccountFilter] = useState(null);
  const [year, setYear] = useState(currentYear);
  const [annualType, setAnnualType] = useState(null);

  // Modal state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Search and pagination
  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();
  const previousSearchRef = useRef(input.queryString || '');

  // Back navigation handler
  const handleBack = () => {
    // Check if coming from account hub (has account_id in URL)
    const accountId = input.account_id;
    if (accountId) {
      history.push(`${routePrefix}/accounts/${accountId}/annuals`);
    } else {
      history.goBack();
    }
  };

  // Get account_id from URL params and pre-select account filter
  const { data: accountsData } = useGetAllAccounts();

  useEffect(() => {
    if (input.account_id && accountsData?.getAllAccounts && !accountFilter) {
      const account = accountsData.getAllAccounts.find(
        (acc) => String(acc.id) === String(input.account_id)
      );
      if (account) {
        setAccountFilter({ value: account.id, label: account.business_dba });
      }
    }
  }, [input.account_id, accountsData, accountFilter, setAccountFilter]);

  // Fetch annual tickets
  const { res, loading } = useGetAllAnnualTickets(
    accountFilter ? accountFilter.value : null,
    null,
    null,
    annualType ? annualType : null,
    null,
    year ? +year : null
  );

  const duplicateAnnual = useDuplicateAnnual();

  const tickets = res?.getAllAnnualTickets?.results ?? [];
  const totalCount = res?.getAllAnnualTickets?.count ?? 0;
  const totalPages = Math.ceil(totalCount / 15);

  // Handlers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleSearch = (searchTerm) => {
    if (searchTerm !== previousSearchRef.current) {
      previousSearchRef.current = searchTerm;
      updateQuery({ queryString: searchTerm, page: 1 });
    }
  };

  const handleViewTicket = (ticket) => {
    const params = ticket.ticket?.id
      ? `?ticketId=${ticket.ticket.id}&ticketName=${encodeURIComponent(
          ticket.name || ''
        )}`
      : '';
    history.push(
      `${routePrefix}/annualUsers/${
        ticket.account?.id || ''
      }/${encodeURIComponent(ticket.account?.business_dba || '')}${params}`
    );
  };

  const handleViewTransactions = (ticket) => {
    const url = `${routePrefix}/annualPurchases/purchase/${
      ticket.id
    }?ticketName=${encodeURIComponent(
      ticket.name
    )}&accountName=${encodeURIComponent(
      ticket.account?.business_dba || ''
    )}&account_id=${ticket.account?.id || ''}`;
    history.push(url);
  };

  const handleEditTicket = (ticket) => {
    history.push(`${routePrefix}/editAnnualTicket/${ticket.id}`);
  };

  const handleDeleteClick = (ticket) => {
    setTicketToDelete(ticket);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ticketToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteAnnualTicket(
        ticketToDelete.id,
        accountFilter?.value
      );

      if (!response || response.errors) {
        toast.error('Error Deleting Annual Ticket');
      } else {
        toast.success('Annual Ticket Successfully Deleted');
      }
    } catch (error) {
      toast.error('Error Deleting Annual Ticket');
    } finally {
      setIsDeleting(false);
      setDeleteModalVisible(false);
      setTicketToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setTicketToDelete(null);
  };

  const handleDuplicateTicket = async (ticket) => {
    if (
      window.confirm('Are you sure you want to duplicate this Annual Ticket?')
    ) {
      const response = await duplicateAnnual(ticket.id, {
        ...(accountFilter ? { accountFilter: accountFilter.value } : null),
        ...(annualType ? { annualType: annualType.value } : null),
      });
      if (!response || response.errors) {
        toast.error('Could not duplicate this Annual Ticket');
      } else {
        toast.success('Annual Ticket successfully duplicated');
      }
    }
  };

  const handleCreateAnnual = () => {
    const addUrl = accountFilter
      ? `${routePrefix}/annualTickets/add?account_id=${accountFilter.value}`
      : `${routePrefix}/annualTickets/add`;
    history.push(addUrl);
  };

  // Table columns
  const columns = [
    {
      field: 'track',
      header: 'Track / Series',
      render: (ticket) => {
        const trackName =
          ticket.tracks?.[0]?.name || ticket.series?.[0]?.name || '—';
        return trackName;
      },
    },
    {
      field: 'account',
      header: 'Account',
      render: (ticket) => ticket.account?.business_dba || '—',
    },
    {
      field: 'name',
      header: 'Annual / Season Ticket Name',
      render: (ticket) => <TicketName>{ticket.name}</TicketName>,
    },
    {
      field: 'event',
      header: 'Event',
      render: (ticket) => ticket.events?.[0]?.name || '—',
    },
    {
      field: 'offSale',
      header: 'Off Sale',
      render: (ticket) => {
        const saleEnd = ticket.ticket?.sale_end;
        return saleEnd
          ? moment(saleEnd, 'MM-DD-YYYY')
              .format('ddd MMM D - YYYY')
              .toUpperCase()
          : '—';
      },
    },
    {
      field: 'status',
      header: 'Status',
      render: (ticket) => (
        <StatusBadge status={ticket.status}>{ticket.status || '—'}</StatusBadge>
      ),
    },
  ];

  // Table actions
  const actions = [
    {
      type: 'view',
      title: 'Members',
      onClick: handleViewTicket,
    },
    {
      type: 'credit',
      title: 'Transactions',
      onClick: handleViewTransactions,
    },
    {
      type: 'edit',
      title: 'Edit',
      onClick: handleEditTicket,
    },
    {
      type: 'duplicate',
      title: 'Duplicate',
      icon: <DuplicateIcon />,
      onClick: handleDuplicateTicket,
    },
    {
      type: 'delete',
      title: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  // Quick actions for sidebar
  const quickActions = [
    {
      label: 'Create Annual',
      icon: <PlusIcon />,
      onClick: handleCreateAnnual,
    },
  ];

  return (
    <EnhancedThemeProvider>
      <PageWrapper>
        <Container>
          {/* Header Card */}
          <Card style={{ marginBottom: '30px' }}>
            <HeaderContainer>
              <BackButton onClick={handleBack} title="Go back">
                <BackArrowIcon />
              </BackButton>
              <PageHeaderIcon icon="events" />
              <HeaderTitle>Annual / Season Tickets</HeaderTitle>
            </HeaderContainer>
          </Card>

          {/* Dashboard Layout */}
          <DashboardLayout>
            <MainContent>
              {/* Data Table Card */}
              <Card noPadding>
                <ContentWrapper>
                  {/* Search + Filters Row */}
                  <PageHeader>
                    <SearchInput
                      onSearch={handleSearch}
                      placeholder="Search tickets..."
                    />
                    <FilterRow>
                      <FilterContainer>
                        <ThemedSelect
                          placeholder="Year"
                          options={[
                            {
                              value: currentYear - 2,
                              label: String(currentYear - 2),
                            },
                            {
                              value: currentYear - 1,
                              label: String(currentYear - 1),
                            },
                            { value: currentYear, label: String(currentYear) },
                            {
                              value: currentYear + 1,
                              label: String(currentYear + 1),
                            },
                          ]}
                          value={{ value: year, label: String(year) }}
                          onChange={(selected) =>
                            setYear(selected?.value || currentYear)
                          }
                        />
                      </FilterContainer>
                      <FilterContainer>
                        <ThemedSelect
                          placeholder="Annual Type"
                          options={[
                            { value: 'membership', label: 'Memberships' },
                            { value: 'annual', label: 'Annual Tickets' },
                            { value: 'registration', label: 'Registration' },
                          ]}
                          value={annualType}
                          onChange={(selected) => setAnnualType(selected)}
                          isClearable
                        />
                      </FilterContainer>
                    </FilterRow>
                  </PageHeader>

                  <DataTable
                    columns={columns}
                    data={tickets}
                    loading={loading}
                    keyField="id"
                    emptyMessage="No annual tickets found"
                    emptySubtext="Try adjusting your filters or create a new annual ticket"
                    emptyIcon={
                      <svg
                        width="48"
                        height="48"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        style={{ marginBottom: '16px', opacity: 0.5 }}
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                          strokeWidth="1.5"
                        />
                        <line x1="16" y1="2" x2="16" y2="6" strokeWidth="1.5" />
                        <line x1="8" y1="2" x2="8" y2="6" strokeWidth="1.5" />
                        <line
                          x1="3"
                          y1="10"
                          x2="21"
                          y2="10"
                          strokeWidth="1.5"
                        />
                      </svg>
                    }
                    actions={actions}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </ContentWrapper>
              </Card>
            </MainContent>

            <Sidebar>
              <QuickActions title="Quick Actions" actions={quickActions} />
            </Sidebar>
          </DashboardLayout>
        </Container>
      </PageWrapper>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalVisible}
        onClose={handleDeleteCancel}
        title="Delete Annual Ticket"
        message={`Are you sure you want to delete "${ticketToDelete?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </EnhancedThemeProvider>
  );
};

export default compose(DeleteAnnualTicket)(AnnualTicketsPage);
